/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogCardFragment, BlogCategoryFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";

import { HeadingLevelContext, useCosmosComponentContext } from "@components/context";
import {
	Animate,
	BlogCards,
	ContentLargeImageSection,
	CosmosTagProps,
	ResponsiveContainer,
	SectionContainer,
	Wrap,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import { FeedItem } from "./feed-card.types";
import { getBlogCardFragment, getContentLargeImageFragment } from "./feed-card.utils";

const feedCardLargeImageStyle = css({
	"&& .section-container": {
		paddingBlockStart: "eds.dimension.96",
		paddingBlockEnd: "eds.dimension.96",
		smDown: { paddingBlockStart: "eds.dimension.48", paddingBlockEnd: "eds.dimension.48" },
		"& .content-large-image-section > div": {
			gap: "eds.dimension.64",
			lgDown: {
				gap: "eds.dimension.48",
				"& .content-large-image-main-stack-image": {
					order: "1",
					mt: "eds.dimension.48",
					mb: "0",
				},
			},
			"& .content-large-image-main-stack": {
				alignContent: "center",
				"& > div": {
					gap: "eds.dimension.16",
				},
			},
			"& .cosmos-date": {
				textStyle: "headingSM",
				color: "eds.text.primary",
			},
			"& .title": {
				marginBottom: "eds.dimension.16",
			},
			"& .desc-buttons": {
				gap: "eds.dimension.24",
			},
		},
	},
});

export interface FeedCardOrganismProps {
	locales: string[];
	items?: FeedItem[];
	blogCategories?: BlogCategoryFragment[];
	featuredLabel?: string | null;
	enableAnimate?: boolean;
	flipFeatured?: boolean;
	disableFeatured?: boolean;
	disablePaddingBlockEnd?: boolean;
	currentBrand?: string;
	hideCategoryPills?: boolean;
	/** True when a page hero renders above the feed — then the featured card is an h2, not the page h1. */
	hasHero?: boolean;
}

const cardRowStyles = css({
	w: "full",
});

export const FeedCardOrganism = ({
	locales,
	items = [],
	blogCategories = [],
	featuredLabel,
	enableAnimate = true,
	flipFeatured,
	disableFeatured,
	disablePaddingBlockEnd,
	currentBrand,
	hideCategoryPills,
	hasHero,
}: FeedCardOrganismProps): React.JSX.Element | null => {
	const pinnedBadge: CosmosTagProps = {
		label: featuredLabel || "Featured",
		icon: "Star",
	};
	const { feedRootPath = "/feed" } = useCosmosComponentContext();
	const messages = useMessages();
	const readMoreLabel = messages["epic.cosmos.read.more"];

	if (!items || !items.length) return null;

	//When less than 4 cards we simple render them as regular blog cards
	const lessThan4 = items.length < 4;

	let index = 0;
	const rows = [];

	// by default, the first row contains a prominent blog card
	const hasFeaturedRow = !disableFeatured && !lessThan4;
	if (hasFeaturedRow) {
		const item = items[index];
		rows.push(
			<div className={cx("feed-card-content-large-image-section", feedCardLargeImageStyle)}>
				<ContentLargeImageSection
					key={`row-${index}`}
					{...getContentLargeImageFragment(
						locales,
						item,
						blogCategories,
						pinnedBadge,
						readMoreLabel,
						feedRootPath,
						currentBrand,
						hideCategoryPills,
					)}
					flip={!flipFeatured}
					sectionIndex={0}
					hasHero={hasHero}
					enableAnimate={enableAnimate}
				/>
			</div>,
		);
		index += 1;
	}

	// the remaining rows contain three blog cards each
	const remainingCards: BlogCardFragment[] = items.slice(index).map((item) => {
		return getBlogCardFragment(
			locales,
			item,
			blogCategories,
			pinnedBadge,
			feedRootPath,
			currentBrand,
			hideCategoryPills,
		);
	});
	if (remainingCards.length) {
		rows.push(
			<Wrap
				key={`row-${index}-animate-wrap`}
				condition={enableAnimate}
				wrap={(children) => (
					<Animate delay={0.75} type="slide" customCss={css.raw({ flexBasis: "50%", width: "100%" })}>
						{children}
					</Animate>
				)}
			>
				{/* Feed grid article titles are real headings: authored h3, shifted to h2 by this
				    provider so they nest under the featured card / hero h1 (floored at h2 — never a
				    second h1). Keeps their uiLG visual size (variant-driven; only the tag changes). */}
				<HeadingLevelContext.Provider value={{ offset: -1 }}>
					<BlogCards key={`row-${index}`} className={cardRowStyles} cards={remainingCards} titleAs="h3" />
				</HeadingLevelContext.Provider>
			</Wrap>,
		);
	}

	return (
		<div className={cx("feed-card-organism", css({ w: "full" }))}>
			{hasFeaturedRow ? rows[0] : null}

			<ResponsiveContainer>
				<SectionContainer disablePaddingBlockEnd={disablePaddingBlockEnd} disablePaddingBlockStart={hasFeaturedRow}>
					<VStack alignItems="flex-start" gap="eds.dimension.16">
						{hasFeaturedRow ? rows.slice(1) : rows}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</div>
	);
};
